import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useDocumentHead from '../hooks/useDocumentHead';
import database from '../database';
import { processOpeningLines } from '../utils/pgnUtils';
import { slugify } from '../utils/urlUtils';
import { trackOpeningSelection, trackNavigation } from '../utils/gtm';
import { getCards, getProgress, subscribe } from '../utils/srs';
import CourseCard from '../components/catalog/CourseCard';
import AppHeader from '../components/layout/AppHeader';
import Icon from '../components/ui/Icon';

const LIKES_KEY = 'esekyLikedCourses';

const loadLikes = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(LIKES_KEY)) || []);
  } catch (error) {
    return new Set();
  }
};

const EMPTY_FILTERS = { color: [], type: [], progress: [] };

const FILTER_GROUPS = [
  { key: 'color', options: [['white', 'White'], ['black', 'Black']] },
  { key: 'type', options: [['base', 'Eseky'], ['community', 'Community']] },
  { key: 'progress', options: [['new', 'New'], ['in_progress', 'In progress'], ['completed', 'Completed']] }
];

const getProgressState = (progress) => {
  if (progress.learned === 0) return 'new';
  if (progress.learned >= progress.total && progress.perfected >= progress.total) return 'completed';
  return 'in_progress';
};

function HomePage() {
  const navigate = useNavigate();
  const [openings, setOpenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [cards, setCards] = useState(getCards);
  const [likedIds, setLikedIds] = useState(loadLikes);

  // Load openings from database on component mount
  useEffect(() => {
    const loadOpenings = async () => {
      try {
        await database.init();
        const openingsWithLines = await database.getOpeningsWithLines();
        setOpenings(openingsWithLines.map(processOpeningLines));
      } catch (error) {
        console.error('Failed to load openings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadOpenings();
  }, []);

  // Keep progress in sync with training sessions
  useEffect(() => subscribe(() => setCards(getCards())), []);

  const progressById = useMemo(() => {
    const now = Date.now();
    return Object.fromEntries(openings.map(opening => [opening.id, getProgress(opening.lines, cards, now)]));
  }, [openings, cards]);

  const totals = useMemo(() => Object.values(progressById).reduce(
    (sum, progress) => ({
      learned: sum.learned + progress.learned,
      total: sum.total + progress.total,
      due: sum.due + progress.due
    }),
    { learned: 0, total: 0, due: 0 }
  ), [progressById]);

  const toggleFilter = (group, value) => {
    setFilters(prev => ({
      ...prev,
      [group]: prev[group].includes(value) ? prev[group].filter(v => v !== value) : [...prev[group], value]
    }));
  };

  const activeFilterCount = filters.color.length + filters.type.length + filters.progress.length;

  // Filter openings based on search term and filters
  const filteredOpenings = openings.filter(opening => {
    const search = searchTerm.toLowerCase();
    const matchesSearch = opening.name.toLowerCase().includes(search) ||
                         (opening.description || '').toLowerCase().includes(search) ||
                         (opening.authorName || '').toLowerCase().includes(search);
    const matchesColor = !filters.color.length || filters.color.includes(opening.playerColor);
    const matchesType = !filters.type.length || filters.type.includes(opening.isCommunity ? 'community' : 'base');
    const matchesProgress = !filters.progress.length || filters.progress.includes(getProgressState(progressById[opening.id]));
    return matchesSearch && matchesColor && matchesType && matchesProgress;
  });

  const handleOpeningClick = (opening) => {
    // Track opening selection
    trackOpeningSelection(
      opening.name,
      opening.playerColor || 'unknown',
      opening.lines?.length || 0
    );
    
    // Track navigation
    trackNavigation('opening_selected', 'homepage', `/${slugify(opening.name)}`);
    
    navigate(`/${slugify(opening.name)}`);
  };

  const handleToggleLike = useCallback(async (opening) => {
    const liked = likedIds.has(opening.id);
    try {
      const { likes_count: likesCount } = liked
        ? await database.unlikeOpening(opening.id)
        : await database.likeOpening(opening.id);
      const next = new Set(likedIds);
      if (liked) next.delete(opening.id); else next.add(opening.id);
      setLikedIds(next);
      try {
        localStorage.setItem(LIKES_KEY, JSON.stringify([...next]));
      } catch (error) {
        // Likes are a convenience, ignore storage errors
      }
      setOpenings(prev => prev.map(o => (o.id === opening.id ? { ...o, likesCount } : o)));
    } catch (error) {
      console.error('Failed to update like:', error);
    }
  }, [likedIds]);

  // Use document head hook for SEO
  useDocumentHead({
    title: 'Eseky - Learn Chess Openings Interactively',
    description: 'Master chess openings with Eseky\'s interactive training platform. Learn Sicilian Defense, Ruy Lopez, Queen\'s Gambit, and more with step-by-step guidance and practice.',
    keywords: 'eseky, chess openings, chess training, learn chess, interactive chess, chess courses, Sicilian Defense, Ruy Lopez, Queen\'s Gambit, King\'s Indian Defense',
    canonical: '/',
    ogTitle: 'Eseky - Learn Chess Openings Interactively',
    ogDescription: 'Master chess openings with Eseky\'s interactive training platform. Learn popular openings with step-by-step guidance.',
    ogUrl: '/',
    twitterTitle: 'Eseky - Learn Chess Openings Interactively',
    twitterDescription: 'Master chess openings with Eseky\'s interactive training platform. Learn popular openings with step-by-step guidance.',
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Eseky - Chess Opening Courses",
        "url": "/",
        "description": "Master chess openings with Eseky's interactive training platform. Learn Sicilian Defense, Ruy Lopez, Queen's Gambit, and more.",
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": "/?search={search_term_string}"
          },
          "query-input": "required name=search_term_string"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Eseky",
          "description": "Interactive chess training platform"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        "name": "Eseky - Chess Opening Courses",
        "url": "/",
        "description": "Interactive chess training platform offering comprehensive chess opening courses",
        "courseOffered": openings.map(opening => ({
          "@type": "Course",
          "name": opening.name,
          "description": opening.description,
          "educationalLevel": opening.difficulty || "Beginner",
          "url": `/opening/${opening.id}`,
          "provider": {
            "@type": "Organization",
            "name": "Eseky - Chess Opening Courses"
          }
        }))
      }
    ]
  });

  const header = (
    <AppHeader
      right={totals.total > 0 && (
        <>
          <span className="hidden md:inline-flex chip cursor-default hover:border-ink-700 hover:text-ink-300" title="Lines learned across all courses">
            <Icon name="brain" size={14} /> {totals.learned}/{totals.total}
          </span>
          {totals.due > 0 && (
            <span className="chip cursor-default border-amber-500/40 bg-amber-500/10 text-amber-300 hover:border-amber-500/40 hover:text-amber-300">
              <Icon name="repeat" size={14} /> {totals.due} to review
            </span>
          )}
        </>
      )}
    />
  );

  if (loading) {
    return (
      <div className="min-h-screen min-h-[100dvh]">
        {header}
        <div className="max-w-7xl mx-auto px-4 py-10 grid grid-cols-1 lg:grid-cols-2 gap-4" aria-busy="true">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="card h-44 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen min-h-[100dvh]">
      {header}

      <main className="max-w-7xl mx-auto px-4 pb-16">
        {/* Hero */}
        <section className="pt-10 pb-8 sm:pt-14 sm:pb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-400 mb-3">Opening trainer</p>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white max-w-3xl leading-[1.1]">
            Learn your openings.<br className="hidden sm:block" /> <span className="text-brand-300">Remember them</span> for good.
          </h1>
          <p className="mt-4 text-ink-300 text-base sm:text-lg max-w-2xl">
            Play every line on the board, then lock it in with spaced repetition: each line comes back right before you would forget it.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href="#courses" className="btn-primary">Browse {openings.length} courses <Icon name="arrowRight" size={16} /></a>
            <Link to="/course-creator/new" className="btn-secondary"><Icon name="plus" size={16} /> Create a course</Link>
          </div>
        </section>

        {/* Toolbar */}
        <section id="courses" className="scroll-mt-20">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-5">
            <div className="relative lg:w-80">
              <Icon name="search" size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-500" />
              <input
                type="search"
                placeholder="Search openings, authors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
                aria-label="Search openings"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 lg:pb-0 -mx-4 px-4 lg:mx-0 lg:px-0">
              {FILTER_GROUPS.map((group, groupIndex) => (
                <React.Fragment key={group.key}>
                  {groupIndex > 0 && <span className="w-px bg-ink-700 my-1 flex-shrink-0" />}
                  {group.options.map(([value, label]) => (
                    <button
                      key={value}
                      onClick={() => toggleFilter(group.key, value)}
                      className={`chip whitespace-nowrap ${filters[group.key].includes(value) ? 'chip-active' : ''}`}
                      aria-pressed={filters[group.key].includes(value)}
                    >
                      {group.key === 'type' && value === 'community' && <Icon name="globe" size={13} />}
                      {label}
                    </button>
                  ))}
                </React.Fragment>
              ))}
            </div>
            <div className="lg:ml-auto text-sm text-ink-400 whitespace-nowrap">
              {filteredOpenings.length} course{filteredOpenings.length !== 1 ? 's' : ''}
              {activeFilterCount > 0 && (
                <button onClick={() => setFilters(EMPTY_FILTERS)} className="ml-2 text-brand-300 hover:text-brand-200 font-semibold">Clear</button>
              )}
            </div>
          </div>

          {filteredOpenings.length === 0 ? (
            <div className="card text-center py-16 px-4">
              <div className="text-ink-300 text-lg font-semibold mb-1">
                {searchTerm || activeFilterCount ? 'No course matches your search' : 'No course yet'}
              </div>
              <p className="text-ink-400 text-sm mb-5">Try other filters, or build your own repertoire.</p>
              <div className="flex justify-center gap-3">
                {(searchTerm || activeFilterCount > 0) && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setFilters(EMPTY_FILTERS);
                    }}
                    className="btn-secondary"
                  >
                    Clear filters
                  </button>
                )}
                <Link to="/course-creator/new" className="btn-primary"><Icon name="plus" size={16} /> Create a course</Link>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filteredOpenings.map(opening => (
                <CourseCard
                  key={opening.id}
                  opening={opening}
                  progress={progressById[opening.id]}
                  liked={likedIds.has(opening.id)}
                  onOpen={() => handleOpeningClick(opening)}
                  onToggleLike={() => handleToggleLike(opening)}
                />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default HomePage;
